import { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { getOrderDetails } from '../api/order.api';
import { toast } from 'react-toastify';
import { API_URL } from '../config';
import { getOrderPriceSummary } from '../utils/orderPricing';

const formatKES = (amount) => 'KES ' + Number(amount || 0).toLocaleString('en-KE', { minimumFractionDigits: 2 });

const PAYMENT_METHOD_LABELS = {
  mpesa: 'M-Pesa',
  pesapal: 'Pesapal',
  visa: 'Visa Card',
  mastercard: 'Mastercard',
  mosmos: 'Mosmos',
  cod: 'Cash on Delivery',
  invoice: 'Invoice',
  bank_transfer: 'Bank Transfer',
};

const getPaymentMethodLabel = (method) => PAYMENT_METHOD_LABELS[method] || 'Cash on Delivery';

export default function OrderConfirmation() {
  const { orderNumber } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useContext(AuthContext);
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = 'Order Confirmed | Fone Express';
    fetchOrderDetails();
  }, [orderNumber]);

  // Poll every 5 s while the Pesapal payment is still pending confirmation
  useEffect(() => {
    if (!order) return;
    if (order.paymentMethod !== 'pesapal' || order.paymentStatus !== 'pesapal_pending') return;

    const interval = setInterval(() => fetchOrderDetails(true), 5000);
    return () => clearInterval(interval);
  }, [order?.paymentMethod, order?.paymentStatus]);

  const fetchOrderDetails = async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      const res = await getOrderDetails(orderNumber);
      if (res.data?.isOk && res.data?.data) {
        setOrder(res.data.data);
      } else if (!silent) {
        toast.error('Failed to load order details');
        setTimeout(() => navigate('/'), 2000);
      }
    } catch (err) {
      console.error('Error fetching order:', err);
      if (!silent) {
        toast.error('Failed to load order details');
        setTimeout(() => navigate('/'), 2000);
      }
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const getImageUrl = (imagePath) => {
    if (!imagePath) return '';
    if (imagePath.startsWith('http')) return imagePath;
    return `${API_URL}/${imagePath}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-muted pt-[10px] pb-12">
        <div className="max-w-3xl mx-auto px-4">
          <div className="flex items-center justify-center py-20">
            <div className="text-center">
              <div className="w-12 h-12 border-4 border-border border-t-primary rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-muted-foreground">Loading order details...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!order) {
    return null;
  }

  return (
    <div className="min-h-screen bg-muted pt-[10px] pb-12">
      <div className="max-w-3xl mx-auto px-4">
        {/* Success Icon */}
        <div className="text-center mb-8">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center
                          justify-center mx-auto mb-4">
            <i className="ri-check-line text-4xl text-green-600"></i>
          </div>
          <h1 className="text-3xl font-bold text-foreground mb-2">
            Order Confirmed!
          </h1>
          <p className="text-muted-foreground">
            Thank you for your purchase
          </p>
        </div>

        {/* Order Details Card */}
        <div className="bg-card rounded-lg shadow-md p-6 mb-6">
          <div className="border-b border-border pb-4 mb-4">
            <div className="flex justify-between items-center flex-wrap gap-4">
              <div>
                <p className="text-sm text-muted-foreground">Order Number</p>
                <p className="text-xl font-bold text-foreground">
                  {order.orderNumber}
                </p>
              </div>
              <div className="text-right">
                <p className="text-sm text-muted-foreground">Total Amount</p>
                <p className="text-xl font-bold text-primary">
                  {formatKES(order.pricing?.grandTotal)}
                </p>
              </div>
            </div>
          </div>

          {/* Payment Method */}
          <div className="mb-4 pb-4 border-b border-border">
            <p className="text-sm text-muted-foreground mb-1">Payment Method</p>
            <p className="text-base font-medium text-foreground">
              {getPaymentMethodLabel(order.paymentMethod)}
            </p>
            {order.paymentStatus === 'paid' && (
              <p className="text-sm text-green-600 mt-1">
                <i className="ri-checkbox-circle-line mr-1"></i>
                Payment Confirmed
              </p>
            )}
            {order.paymentMethod === 'pesapal' && order.paymentStatus === 'pesapal_pending' && (
              <p className="text-sm text-yellow-600 mt-1">
                <i className="ri-time-line mr-1"></i>
                Payment pending confirmation — you will receive an update shortly.
              </p>
            )}
          </div>

          {/* Store Pickup Transfer Status */}
          {order.fulfillmentType === 'store_pickup' && order.pickup && (
            <div className="mb-6 p-4 bg-orange-50 border border-orange-100 rounded-lg">
              <h3 className="font-bold text-foreground mb-3 flex items-center gap-2">
                <i className="ri-store-2-line text-primary"></i>
                Store Pickup Details
              </h3>
              
              <div className="flex items-start gap-3 mb-4">
                <i className="ri-map-pin-2-line text-primary mt-0.5"></i>
                <div>
                  <p className="font-semibold text-sm">{order.pickup.storeName}</p>
                  {order.pickup.storeAddress && (
                    <p className="text-xs text-muted-foreground mt-0.5">{order.pickup.storeAddress}</p>
                  )}
                </div>
              </div>

              {/* Transfer Steps */}
              <div className="mt-6 pt-6 border-t border-orange-200">
                <p className="text-sm font-bold text-orange-800 mb-4">Pickup Progress</p>
                {(() => {
                  const pickupStatus = order.pickup.status || 'pending';
                  const transfer = order.pickup.transferId;
                  const transferStatus = typeof transfer === 'object' ? (transfer?.status || 'pending') : 'pending';
                  const approvalStatus = typeof transfer === 'object' ? (transfer?.approval_status || 'pending') : 'pending';

                  const steps = [
                    {
                      key: 'order_placed',
                      label: 'Order Confirmed',
                      icon: 'ri-shopping-bag-line',
                      done: true,
                    },
                    {
                      key: 'awaiting_approval',
                      label: 'Admin Approval',
                      icon: 'ri-admin-line',
                      done: !!transfer && approvalStatus === 'approved',
                      active: !!transfer && approvalStatus === 'pending',
                    },
                    {
                      key: 'in_transit',
                      label: 'Stock in Transit',
                      icon: 'ri-truck-line',
                      done: ['shipped', 'received'].includes(transferStatus),
                      active: transferStatus === 'shipped',
                    },
                    {
                      key: 'ready',
                      label: 'Ready for Pickup',
                      icon: 'ri-store-2-line',
                      done: ['ready', 'collected'].includes(pickupStatus),
                      active: pickupStatus === 'ready',
                    }
                  ];

                  return (
                    <div className="space-y-4">
                      {steps.map((step, idx) => (
                        <div key={step.key} className="flex gap-4 items-start relative">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center z-10 shrink-0 border-2 ${
                            step.done 
                              ? 'bg-green-500 border-green-500 text-white' 
                              : step.active 
                                ? 'bg-primary border-primary text-white' 
                                : 'bg-white border-orange-200 text-orange-200'
                          }`}>
                            <i className={`${step.done ? 'ri-check-line' : step.icon} text-sm ${step.active ? 'animate-pulse' : ''}`}></i>
                          </div>
                          {idx < steps.length - 1 && (
                            <div className={`absolute left-4 top-8 w-0.5 h-4 -ml-px ${steps[idx + 1].done ? 'bg-green-500' : 'bg-orange-200'}`}></div>
                          )}
                          <div className="pt-1">
                            <p className={`text-sm font-bold ${step.done ? 'text-green-700' : step.active ? 'text-primary' : 'text-orange-300'}`}>
                              {step.label}
                            </p>
                            {step.active && <p className="text-[10px] text-primary/80">In progress...</p>}
                          </div>
                        </div>
                      ))}
                    </div>
                  );
                })()}
              </div>
              
              {order.pickup.status === 'ready' && (
                <div className="mt-4 p-2 bg-green-100 border border-green-200 rounded text-xs text-green-800">
                  <i className="ri-information-line mr-1"></i>
                  Your order is ready for pickup! Please bring your ID.
                </div>
              )}
            </div>
          )}

          {/* Order Items */}
          <div>
            <h3 className="font-bold text-foreground mb-4">Order Items</h3>
            <div className="space-y-4">
              {order.items?.map((item) => (
                <div key={item._id} className="flex gap-4">
                  <img
                    src={getImageUrl(item.image)}
                    alt={item.productName}
                    className="w-20 h-20 object-cover rounded border border-border"
                  />
                  <div className="flex-1">
                    <h4 className="font-medium text-foreground">
                      {item.productName}
                    </h4>
                    <p className="text-sm text-muted-foreground">{item.variantLabel}</p>
                    <p className="text-sm text-foreground mt-1">
                      Qty: {item.quantity} × {formatKES(item.unitPrice)}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-medium text-foreground">
                      {formatKES(item.lineTotal)}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            {/* Price Breakdown */}
            <div className="mt-6 pt-4 border-t border-border space-y-2 text-sm">
              {(() => {
                const priceSummary = getOrderPriceSummary(order);
                return priceSummary.summaryRows.map((row) => (
                  <div key={row.label} className={`flex justify-between ${row.isInformational ? 'text-xs' : ''}`}>
                    <span className={
                      row.isDiscount
                        ? 'text-green-600'
                        : row.isInformational
                          ? 'text-muted-foreground/80'
                          : 'text-muted-foreground'
                    }>
                      {row.label}
                    </span>
                    <span className={
                      row.isDiscount
                        ? 'text-green-600'
                        : row.isInformational
                          ? 'text-muted-foreground/80'
                          : 'text-foreground'
                    }>
                      {row.isDiscount ? `-${formatKES(Math.abs(row.value))}` : formatKES(row.value)}
                    </span>
                  </div>
                ));
              })()}
              <div className="flex justify-between">
                <span className="text-muted-foreground">Shipping</span>
                <span className="text-foreground">
                  {order.pricing?.shippingCharge > 0 ? formatKES(order.pricing.shippingCharge) : 'Free'}
                </span>
              </div>
              {order.pricing?.storeCreditApplied > 0 && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Store Credit</span>
                  <span className="text-green-600">-{formatKES(order.pricing.storeCreditApplied)}</span>
                </div>
              )}
              {order.pricing?.loyaltyPointsDiscount > 0 && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Loyalty Points</span>
                  <span className="text-green-600">-{formatKES(order.pricing.loyaltyPointsDiscount)}</span>
                </div>
              )}
              <div className="flex justify-between pt-2 border-t border-border font-semibold text-base">
                <span className="text-foreground">Grand Total</span>
                <span className="text-primary">{formatKES(order.pricing?.grandTotal)}</span>
              </div>
            </div>
          </div>

          {/* Shipping Address */}
          <div className="mt-6 pt-6 border-t border-border">
            <h3 className="font-bold text-foreground mb-3">Shipping Address</h3>
            <div className="text-sm text-muted-foreground">
              <p className="font-medium text-foreground">{order.shippingAddress?.fullName}</p>
              <p>{order.shippingAddress?.phone}</p>
              <p>{order.shippingAddress?.addressLine1}</p>
              {order.shippingAddress?.addressLine2 && <p>{order.shippingAddress.addressLine2}</p>}
              <p>
                {order.shippingAddress?.city}, {order.shippingAddress?.state} {order.shippingAddress?.postalCode}
              </p>
              <p>{order.shippingAddress?.country}</p>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="space-y-3">
          {isAuthenticated ? (
            <Link
              to={`/account/orders/${orderNumber}`}
              className="block w-full bg-primary text-white text-center py-3
                        rounded-lg hover:bg-[#D84315] transition-colors font-medium"
            >
              View Order Details
            </Link>
          ) : (
            order.guestTrackingToken && (
              <Link
                to={`/track-order/${order.guestTrackingToken}`}
                className="block w-full bg-primary text-white text-center py-3
                          rounded-lg hover:bg-[#D84315] transition-colors font-medium"
              >
                Track Your Order
              </Link>
            )
          )}
          <Link
            to="/"
            className="block w-full border-2 border-border text-foreground text-center
                      py-3 rounded-lg hover:bg-muted transition-colors font-medium"
          >
            Continue Shopping
          </Link>
        </div>
      </div>
    </div>
  );
}
